import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  LayoutDashboard, Briefcase, Users, Ticket, IndianRupee, Calendar, Package, TrendingUp,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";

const COLORS = ["#006666", "#FF6600", "#10B981", "#3B82F6", "#8B5CF6", "#F59E0B"];

function StatCard({ icon: Icon, label, value, sub, color = "teal", testid }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5" data-testid={testid || `stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{label}</div>
          <div className="font-display text-3xl font-bold">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color === "teal" ? "bg-[#E5F0F0] text-teal" : "bg-[#FFF1E6] text-saffron"}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const { permissions } = usePermissions();

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/bookings").then((r) => setBookings(r.data));
    api.get("/admin/customers").then((r) => setCustomers(r.data));
    api.get("/admin/vendors").then((r) => setVendors(r.data));
  };

  useEffect(load, []);

  const updateBookingStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const approveVendor = async (vid, status) => {
    try {
      await api.put(`/admin/vendors/${vid}/approve`, { status });
      toast.success(`Vendor ${status}`);
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  if (!stats) return <div className="p-12 text-center text-muted-foreground">Loading dashboard...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-teal text-white flex items-center justify-center">
            <LayoutDashboard className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl">Admin Dashboard</h1>
            <p className="text-muted-foreground text-sm">Overview & operations</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/products"><Button variant="outline" data-testid="link-admin-products">Manage Products</Button></Link>
          {permissions.settings && (
            <Link to="/admin/settings"><Button className="bg-teal hover:bg-teal-dark text-white" data-testid="link-admin-settings">Site Settings</Button></Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={IndianRupee} label="Total Revenue" value={formatINR(stats.total_revenue)} sub="All time, paid" color="saffron" testid="stat-revenue" />
        <StatCard icon={Calendar} label="Today's Bookings" value={stats.bookings_today} sub={`${stats.bookings_total} total`} testid="stat-bookings" />
        <StatCard icon={Users} label="Customers" value={stats.customers} sub={`${stats.vendors} vendors`} testid="stat-customers" />
        <StatCard icon={Package} label="Products" value={stats.products} sub="Active listings" color="saffron" testid="stat-products" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-display text-lg mb-4">Bookings by Category</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.bookings_by_category}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E0E8E8" />
              <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#006666" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <h3 className="font-display text-lg mb-4">Product Catalog Distribution</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={stats.categories} dataKey="count" nameKey="_id" innerRadius={45} outerRadius={90} paddingAngle={2}>
                {stats.categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <Tabs defaultValue="bookings">
        <TabsList className="w-full justify-start bg-white border border-border">
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="admin-tab-vendors">Vendors</TabsTrigger>
          <TabsTrigger value="customers" data-testid="admin-tab-customers">Customers</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
          <div className="bg-white border border-border rounded-2xl overflow-hidden mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Travel</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow key={b.id} data-testid={`admin-booking-${b.reference_no}`}>
                    <TableCell className="font-mono text-xs">{b.reference_no}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{b.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{b.contact_email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{b.product_title}</div>
                      <div className="text-xs text-muted-foreground capitalize">{b.product_category}</div>
                    </TableCell>
                    <TableCell className="text-sm">{b.travel_date}</TableCell>
                    <TableCell className="font-medium text-saffron">{formatINR(b.total_amount)}</TableCell>
                    <TableCell><Badge variant={b.payment_status === "paid" ? "default" : "outline"} className={b.payment_status === "paid" ? "bg-success text-white" : ""}>{b.payment_status}</Badge></TableCell>
                    <TableCell>
                      <Select value={b.status} onValueChange={(v) => updateBookingStatus(b.id, v)}>
                        <SelectTrigger className="h-8 text-xs w-36" data-testid={`status-${b.reference_no}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["pending", "confirmed", "voucher_issued", "completed", "cancelled", "refunded"].map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
                {bookings.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">No bookings yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="vendors">
          <div className="bg-white border border-border rounded-2xl overflow-hidden mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.map((v) => (
                  <TableRow key={v.id} data-testid={`admin-vendor-${v.id}`}>
                    <TableCell className="font-medium">{v.name}</TableCell>
                    <TableCell>{v.business_name || "-"}</TableCell>
                    <TableCell className="text-sm">{v.email}</TableCell>
                    <TableCell className="font-mono text-xs">{v.gstin || "-"}</TableCell>
                    <TableCell><Badge className={v.status === "approved" ? "bg-success text-white" : "bg-amber-500 text-white"}>{v.status || "pending"}</Badge></TableCell>
                    <TableCell>
                      {v.status !== "approved" && <Button size="sm" variant="outline" onClick={() => approveVendor(v.id, "approved")} data-testid={`approve-${v.id}`}>Approve</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {vendors.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-12">No vendors</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="customers">
          <div className="bg-white border border-border rounded-2xl overflow-hidden mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-sm">{c.email}</TableCell>
                    <TableCell className="text-sm">{c.phone || "-"}</TableCell>
                    <TableCell className="text-xs">{c.created_at?.slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
                {customers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-12">No customers</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
